// Etat d'authentification partage par toute l'application.
//
// Au chargement, si un token traine dans localStorage, on demande au
// SERVEUR qui il est (/auth/me.php). On ne se fie jamais a ce qui est
// stocke cote navigateur pour decider qu'un compte est valide ou payant.
import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import { authApi, getToken, setToken, clearToken } from './api'

const AuthContext = createContext(null)

export function AuthProvider ({ children }) {
  const [user, setUser] = useState(null)
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  // Relit l'utilisateur depuis le serveur.
  const refresh = useCallback(async () => {
    if (!getToken()) { setUser(null); setLoading(false); return null }
    try {
      const data = await authApi.me()
      setUser(data.user)
      setStats(data.stats)
      return data.user
    } catch {
      clearToken()
      setUser(null)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  // Si le compte n'a pas encore de fuseau, on le cale SILENCIEUSEMENT sur celui
  // du navigateur (une seule fois). Les couvre-feux Cloudflare en dépendent —
  // sans lui, la règle retombe sur l'heure locale déduite de l'IP (fallback ok).
  const tzTried = useRef(false)
  useEffect(() => {
    if (!user || user.timezone || tzTried.current) return
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!tz) return
    tzTried.current = true
    authApi.setTimezone(tz)
      .then(() => setUser(u => (u ? { ...u, timezone: tz } : u)))
      .catch(() => { /* silencieux : le fallback IP reste actif côté Cloudflare */ })
  }, [user])

  async function login (email, password) {
    const data = await authApi.login(email, password)
    setToken(data.token)
    await refresh()
    return data.user
  }

  async function register (payload) {
    const data = await authApi.register(payload)
    setToken(data.token)
    await refresh()
    return data.user
  }

  // Premiere connexion : reclame un compte cree par un achat Shopify (email +
  // mot de passe), puis connecte comme un login.
  async function claim (email, password) {
    const data = await authApi.claim(email, password)
    setToken(data.token)
    await refresh()
    return data.user
  }

  async function logout () {
    try { await authApi.logout() } catch { /* le token est peut-etre deja mort */ }
    clearToken()
    setUser(null)
    setStats(null)
  }

  return (
    <AuthContext.Provider value={{ user, stats, loading, login, register, claim, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth () {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth doit être utilisé dans <AuthProvider>')
  return ctx
}
